import { useEffect, useState } from "react";
import AdminLayout from "@/components/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Slider } from "@/components/ui/slider";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CHEM_VESSELS } from "@/data/chemCarriageData";
import { PLAN_TANKS, GROUP_FILL, GROUP_LABEL, type TankGroup } from "@/data/tankPlanLayout";
import { getLevels, setTankLevel, setVesselLevels, resetAllLevels, useTankLevels } from "@/data/tankLevelsStore";
import { InteractiveTankPlan } from "@/components/chem/InteractiveTankPlan";
import { toast } from "sonner";
import { RotateCcw, Droplets } from "lucide-react";

const GROUPS: TankGroup[] = ["cargo", "ballast", "fuel", "service", "slop", "void", "machinery"];

const VesselEditor = ({ vesselName }: { vesselName: string }) => {
  const levels = useTankLevels(vesselName);
  const vessel = CHEM_VESSELS.find(v => v.name === vesselName)!;

  const fillable = PLAN_TANKS.filter(t => t.fillable);

  const setAll = (group: TankGroup, pct: number) => {
    const next = { ...levels };
    fillable.filter(t => t.group === group).forEach(t => { next[t.id] = pct; });
    setVesselLevels(vesselName, next);
    toast.success(`${GROUP_LABEL[group]} set to ${pct}% on ${vesselName}`);
  };

  return (
    <div className="space-y-6">
      <InteractiveTankPlan vessel={vessel} />

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Droplets className="w-4 h-4 text-teal" /> Bulk fill / drain
          </CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-2">
          {GROUPS.filter(g => g !== "void" && g !== "machinery").map(g => (
            <div key={g} className="flex items-center gap-1.5 rounded-md border border-border/60 px-2 py-1">
              <span className="w-2.5 h-2.5 rounded-sm" style={{ background: GROUP_FILL[g] }} />
              <span className="text-[11px] font-medium">{GROUP_LABEL[g]}</span>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setAll(g, 0)}>0%</Button>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setAll(g, 50)}>50%</Button>
              <Button size="sm" variant="ghost" className="h-6 px-2 text-[10px]" onClick={() => setAll(g, 95)}>95%</Button>
            </div>
          ))}
        </CardContent>
      </Card>

      {GROUPS.map(group => {
        const group_tanks = fillable.filter(t => t.group === group);
        if (!group_tanks.length) return null;
        return (
          <Card key={group}>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm flex items-center gap-2">
                <span className="w-3 h-3 rounded-sm" style={{ background: GROUP_FILL[group] }} />
                {GROUP_LABEL[group]}
                <Badge variant="outline" className="text-[10px] ml-1">{group_tanks.length} tanks</Badge>
              </CardTitle>
            </CardHeader>
            <CardContent className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {group_tanks.map(t => {
                const pct = levels[t.id] ?? 0;
                return (
                  <div key={t.id} className="rounded-md border border-border/60 p-3">
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <p className="text-xs font-bold">{t.label}</p>
                        <p className="text-[10px] text-muted-foreground">{t.id} · {t.capacity ?? "—"} {t.unit ?? ""}</p>
                      </div>
                      <Input
                        type="number" min={0} max={100} value={pct}
                        onChange={(e) => setTankLevel(vesselName, t.id, Number(e.target.value))}
                        className="h-7 w-16 text-xs text-right"
                      />
                    </div>
                    <Slider
                      value={[pct]} min={0} max={100} step={1}
                      onValueChange={(v) => setTankLevel(vesselName, t.id, v[0])}
                    />
                    <div className="flex justify-between text-[9px] text-muted-foreground mt-1">
                      <span>0%</span><span>{pct}%</span><span>100%</span>
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
};

const AdminTankLevels = () => {
  // Trigger initial seed read on mount
  useEffect(() => { getLevels(); }, []);
  const [tab, setTab] = useState(CHEM_VESSELS[0].name);

  return (
    <AdminLayout>
      <div className="flex items-start justify-between mb-6 gap-3 flex-wrap">
        <div>
          <h1 className="font-display text-2xl font-bold">Chem Carriage · Tank Levels</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Set live ullage / level for every cargo, ballast, fuel and service tank.
            Updates flow into the Client Portal and Intranet tank-plan in real time.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => { resetAllLevels(); toast.success("Reset to seed defaults"); }}>
          <RotateCcw className="w-3.5 h-3.5 mr-1.5" /> Reset all to defaults
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          {CHEM_VESSELS.map(v => (
            <TabsTrigger key={v.name} value={v.name} className="text-xs">{v.name}</TabsTrigger>
          ))}
        </TabsList>
        {CHEM_VESSELS.map(v => (
          <TabsContent key={v.name} value={v.name} className="mt-6">
            <VesselEditor vesselName={v.name} />
          </TabsContent>
        ))}
      </Tabs>
    </AdminLayout>
  );
};

export default AdminTankLevels;
